import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Label from '../../../../components/label/Label';
import { ROW_SELECTION_OPTIONS } from '../../../../components/richgrid/constants';
import RichGrid from '../../../../components/richgrid/RichGrid';
import { componentKey, setIsSelectedFormsCombinationAllowed, updatePaginationState } from './ReconciliationListingSlice';
import styles from './ReconciliationListing.styles';
import SelectDropdown from '../../../../components/dropdown/selectdropdown/SelectDropdown';
import { checkIfSelectedFormsCombinationAllowed, getReconciliationListData, getReconciliationPayersAndYears, getReconciliationStates, reconciliationUnSubmittedFormsExportToExcel } from './ReconciliationListingSaga';
import Button from '../../../../components/button/Button';
import { BUTTON_TYPE } from '../../../../components/button/constants';
import { addNotifications } from '../../../toaster/ToasterPortalSlice';
import { TOASTER_VARIANT } from '../../../../components/toast/constants';
import useGarbageCollector from '../../../../../libs/garbagecollector/garbageCollectorHooks';
import { VEC_ICON_NAME } from '../../../../components/icons/constants';
import { setIsReconciliationFormSelectedFromListingPage, setReconciliationSelectedFormRecords, setReconciliationSelectedPayer, setReconciliationSelectedState, setReconciliationSelectedTaxYear, setSelectedReferenceNumbersAndFormNames, setPayerDropdownPagination } from '../ReconciliationSlice';
import { componentKey as reconciliationComponentKey } from '../ReconciliationSlice';
import { FORM_SECTION_ROUTES } from '../../constants';
import useTxNavigate from '../../../../../libs/txnavigate/TxNavigateHook';
import { RECONCILIATION_LOADING_STATES, RECONCILIATION_LOADING_STATES_MESSAGE_MAP } from '../constants';
import Loadable from '../../../../components/loadable/Loadable';

export default function ReconciliationListing() {

	useGarbageCollector(componentKey);

	const { paginationState, reconciliationFormsListRecords, payerDropdownOptions, taxYearDropdownOptions, reconciliationStateDropdownOptions, isSelectedFormsCombinationAllowed, loadingState } = useSelector(state => state[componentKey]);
	const { reconciliationSelectedFormRecords, reconciliationSelectedPayer, reconciliationSelectedTaxYear, reconciliationSelectedState, selectedReferenceNumbersAndFormNames, payerDropdownPagination } = useSelector(state => state[reconciliationComponentKey]);
	const dispatch = useDispatch();
	const navigate = useTxNavigate();
	const [paginatedPayerList, setPaginatedPayerList] = useState([]);

	useEffect(() => {
		setPaginatedPayerList(payerDropdownOptions)
	}, [payerDropdownOptions])

	useEffect(() => {
		dispatch(getReconciliationPayersAndYears());
	}, []);

	useEffect(() => {
		if (isSelectedFormsCombinationAllowed) {
			dispatch(setIsReconciliationFormSelectedFromListingPage(true));
			navigate(`/${FORM_SECTION_ROUTES.FORMS}/${FORM_SECTION_ROUTES.RECONCILIATION}/${FORM_SECTION_ROUTES.SUBMITRECONCILIATION}`)
			dispatch(setIsSelectedFormsCombinationAllowed(false));
		}
	}, [isSelectedFormsCombinationAllowed]);

	useEffect(() => {
		if (reconciliationSelectedPayer) {
			dispatch(getReconciliationStates({ payerId: reconciliationSelectedPayer.value }))
		}
	}, [reconciliationSelectedPayer]);

	useEffect(() => {
		if (reconciliationSelectedTaxYear && reconciliationSelectedPayer && reconciliationSelectedState.value !== "") {
			dispatch(getReconciliationListData({
				Payerid: reconciliationSelectedPayer.value,
				taxYear: reconciliationSelectedTaxYear.value,
				state: reconciliationSelectedState.value,
				PageNumber: paginationState.pageNumber,
				PageSize: paginationState.pageSize,
			}));
		}
	}, [reconciliationSelectedTaxYear, reconciliationSelectedState]);

	const handleNxtBtnClk = () => {
		if (reconciliationSelectedFormRecords.length < 1) {
			dispatch(addNotifications({
				heading: "No record has been selected",
				message: "Please select atleast one record to proceed",
				variant: TOASTER_VARIANT.WARNING
			}));
		} else {
			dispatch(checkIfSelectedFormsCombinationAllowed({
				state: reconciliationSelectedState.value,
				formNames: selectedReferenceNumbersAndFormNames?.selectedFormNames,
			}))
		}
	}

	const handleRecordsSelection = (selectedRecord) => {
		dispatch(setReconciliationSelectedFormRecords(selectedRecord));
		const referenceNumbersArray = [];
		const selectedFormNamesArray = [];

		for (const item of selectedRecord) {
			const referenceNumber = item["ReferenceNumber"];
			const selectedFormNames = item["FormName"];
			if (referenceNumber) {
				referenceNumbersArray.push(referenceNumber);
			}
			if (selectedFormNames) {
				selectedFormNamesArray.push(selectedFormNames);
			}
		}
		const referenceNumbersCommaSeparated = referenceNumbersArray.join(",");
		const selectedFormNamesCommaSeparated = selectedFormNamesArray.join(",");
		dispatch(setSelectedReferenceNumbersAndFormNames({
			selectedReferenceNumbers: referenceNumbersCommaSeparated,
			selectedFormNames: selectedFormNamesCommaSeparated,
		}));
	}

	const reconciliationFormsListingColumns = [
		{
			field: "referenceNumber",
			header: <Label>Reference No</Label>,
			renderLogic: (row, idx) => <span>{row?.ReferenceNumber}</span>
		},
		{
			field: "formName",
			header: <Label>Form Name</Label>,
			renderLogic: (row, idx) => <span>{row?.FormName}</span>
		},
		{
			field: "state",
			header: <Label>State</Label>,
			renderLogic: (row, idx) => <span>{row?.State}</span>
		},
		{
			field: "status",
			header: <Label>Status</Label>,
			renderLogic: (row, idx) => <span>{row?.Status}</span>
		}
	];

	return <>
		<div style={styles.mainContainer}>
			<div style={styles.dropdownRowWrapper}>
				<div style={styles.dropdownRow}>
					<div>
						<SelectDropdown
							label={"Payer:"}
							placeHolder={"Select payer"}
							name={"reconciliationSelectedPayer"}
							value={reconciliationSelectedPayer ?? { label: "", value: "" }}
							onSelectCb={(name, selectedOption) => dispatch(setReconciliationSelectedPayer(selectedOption))}
							options={paginatedPayerList.slice(0, payerDropdownPagination.PageSize * (payerDropdownPagination.PageNumber))}
							onDropdownTouchedToBottomCb={() => {
								if (payerDropdownPagination.PageSize * (payerDropdownPagination.PageNumber) < paginatedPayerList?.length) {
									dispatch(setPayerDropdownPagination({ PageNumber: payerDropdownPagination.PageNumber + 1 }))
								}
							}}
							isClearable={false}
						/>
					</div>
					<div>
						<SelectDropdown
							label={"Tax Year:"}
							placeHolder={"Select year"}
							name={"reconciliationSelectedTaxYear"}
							value={reconciliationSelectedTaxYear ?? { label: "", value: "" }}
							onSelectCb={(name, selectedOption) => dispatch(setReconciliationSelectedTaxYear(selectedOption))}
							options={taxYearDropdownOptions}
							isClearable={false}
						/>
					</div>
					<Loadable currentState={loadingState.state} loadingStates={[RECONCILIATION_LOADING_STATES.STATES_FILTER_LOADING]} messageMap={RECONCILIATION_LOADING_STATES_MESSAGE_MAP} isNewLoadable={true} isFullWidth={false}>
						<div>
							<SelectDropdown
								label={"State:"}
								placeHolder={"Select state"}
								name={"reconciliationSelectedState"}
								value={reconciliationSelectedState ?? { label: "", value: "" }}
								onSelectCb={(name, selectedOption) => dispatch(setReconciliationSelectedState(selectedOption))}
								options={reconciliationStateDropdownOptions}
								isClearable={false}
							/>
						</div>
					</Loadable>
				</div>
				<div style={styles.downloadUnsubmittedFormsBtn}>
					<Loadable currentState={loadingState.state} loadingStates={[RECONCILIATION_LOADING_STATES.DOWNLOAD_UNSUBMITTED_STATE_RECONCILLIATION_FORM_LOADING]} messageMap={RECONCILIATION_LOADING_STATES_MESSAGE_MAP} isNewLoadable={true} isFullWidth={false}>
						<Button type={BUTTON_TYPE.PRIMARY_LIGHT_WITH_BORDER} prefixProps={{ icon: VEC_ICON_NAME.DOWNLOAD_ICON_V2 }} onClickCb={() => dispatch(reconciliationUnSubmittedFormsExportToExcel())}>UnSubmitted State Reconciliation Forms</Button>
					</Loadable>
				</div>
			</div>
			<Loadable currentState={loadingState.state} loadingStates={[RECONCILIATION_LOADING_STATES.GRID_LOADING]} messageMap={RECONCILIATION_LOADING_STATES_MESSAGE_MAP} isNewLoadable={true} isFullWidth={false}>
				<RichGrid
					data={reconciliationFormsListRecords}
					columns={reconciliationFormsListingColumns}
					selectable={true}
					extractRowKey={(row) => row.ReferenceNumber}
					onSelectionChangeCallBack={(selectedData) => handleRecordsSelection(selectedData)}
					onHeaderSelectionChangeCallBack={(selectedData) => handleRecordsSelection(selectedData)}
					selectedRows={reconciliationSelectedFormRecords}
					paginationProps={{
						skip: paginationState.skip,
						take: paginationState.pageSize,
						takeOptions: ROW_SELECTION_OPTIONS,
						onChangeTakeCb: (newTake) => {
							dispatch(updatePaginationState({ pageSize: newTake, pageNumber: 1, skip: 0 }));
							dispatch(getReconciliationListData({
								Payerid: reconciliationSelectedPayer.value,
								taxYear: reconciliationSelectedTaxYear.value,
								state: reconciliationSelectedState.value,
								PageNumber: paginationState.pageNumber,
								PageSize: newTake,
							}));
						},
						total: paginationState.total,
						onChange: (take, skip) => {
							dispatch(updatePaginationState({ pageSize: take, skip: skip, pageNumber: Math.ceil((take + skip) / take) }));
							dispatch(getReconciliationListData({
								Payerid: reconciliationSelectedPayer.value,
								taxYear: reconciliationSelectedTaxYear.value,
								state: reconciliationSelectedState.value,
								PageNumber: Math.ceil((take + skip) / take),
								PageSize: take,
							}));
						},
					}}
				/>
			</Loadable>
			<div style={styles.reconciliationFormsNextBtnDiv}>
				<Loadable currentState={loadingState.state} loadingStates={[RECONCILIATION_LOADING_STATES.CHECK_IF_SELECTED_FORMS_COMBINATION_ALLOWED_LOADING]} messageMap={RECONCILIATION_LOADING_STATES_MESSAGE_MAP} isNewLoadable={true} isFullWidth={false}>
					<Button type={BUTTON_TYPE.PRIMARY} onClickCb={handleNxtBtnClk} style={styles.reconciliationFormsNextBtnDivStyles}>Next</Button>
				</Loadable>
			</div>
		</div>
	</>
}